import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';

const selectCls = 'h-10 w-full px-3 text-sm rounded-md border border-line bg-surface text-fg';
const cardCls = 'rounded-lg border border-line bg-surface p-4';

const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

function Section({ title, hint, children, action }) {
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-fg">{title}</h2>
          {hint && <p className="text-sm text-fg-muted mt-0.5">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function ReorderButtons({ index, total, onMove, testid, disabled }) {
  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" className="px-2" disabled={disabled || index === 0}
              onClick={() => onMove(index, index - 1)} title="Move up" data-testid={`${testid}-up`}>
        <ArrowUp className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button variant="ghost" size="sm" className="px-2" disabled={disabled || index === total - 1}
              onClick={() => onMove(index, index + 1)} title="Move down" data-testid={`${testid}-down`}>
        <ArrowDown className="w-4 h-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

/* ── Clients (GSOC's own list) ─────────────────────────────────────────── */
function ClientsTab({ rows, reload, canEdit, canDelete }) {
  const [local, setLocal] = useState(rows);
  const [linkable, setLinkable] = useState([]);
  const [draft, setDraft] = useState({ name: '', linked_client_id: '' });
  useEffect(() => { setLocal(rows); }, [rows]);
  useEffect(() => {
    if (canEdit) api.get('/gsoc/clients/linkable').then(({ data }) => setLinkable(data)).catch(() => {});
  }, [canEdit]);

  const patch = (id, changes) => setLocal((l) => l.map((x) => x.id === id ? { ...x, ...changes } : x));

  const add = async () => {
    if (draft.name.trim().length < 2) return toast.error('Enter a client name');
    try {
      await api.post('/gsoc/clients', {
        name: draft.name.trim(), linked_client_id: draft.linked_client_id || null, is_active: true,
      });
      setDraft({ name: '', linked_client_id: '' });
      toast.success('Client added');
      reload();
    } catch (e) { err(e, 'Could not add the client'); }
  };

  const save = async (r) => {
    try {
      await api.put(`/gsoc/clients/${r.id}`, {
        name: r.name.trim(), linked_client_id: r.linked_client_id || null, is_active: r.is_active,
      });
      await api.put(`/gsoc/client-settings/${r.id}`, {
        activity_log_enabled: r.activity_log_enabled !== false,
        report_recipients: (r.report_recipients || []).map((s) => s.trim()).filter(Boolean),
        timezone: r.timezone || 'America/Chicago',
      });
      toast.success(`${r.name} saved`);
      reload();
    } catch (e) { err(e, 'Could not save the client'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Its ${r.site_count} site(s) and report settings go with it.`)) return;
    try {
      await api.delete(`/gsoc/clients/${r.id}`);
      toast.success(`"${r.name}" deleted`);
      reload();
    } catch (e) { err(e, 'Could not delete the client'); }
  };

  return (
    <Section title="GSOC Clients"
             hint="This list belongs to GSOC. Add clients here — nothing is read from People & Places, and linking to a record over there is optional and for reference only.">
      {canEdit && (
        <div className={`${cardCls} flex flex-wrap gap-3 items-end`}>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-sm">New client name</Label>
            <Input value={draft.name} className="mt-1" placeholder="Riverside Logistics"
                   data-testid="gsoc-client-name-input"
                   onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-sm">Link to a People &amp; Places client (optional)</Label>
            <select className={`${selectCls} mt-1`} value={draft.linked_client_id}
                    data-testid="gsoc-client-link-select"
                    onChange={(e) => setDraft((d) => ({ ...d, linked_client_id: e.target.value }))}>
              <option value="">Not linked</option>
              {linkable.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <Button onClick={add} data-testid="gsoc-client-add-btn"><Plus className="w-4 h-4 mr-1" />Add Client</Button>
        </div>
      )}

      <div className="space-y-2" data-testid="gsoc-clients-list">
        {local.length === 0 && <p className="text-sm text-fg-muted" data-testid="gsoc-clients-empty">No GSOC clients yet.</p>}
        {local.map((r, i) => (
          <div key={r.id} className={`${cardCls} space-y-3`} data-testid={`gsoc-client-row-${i}`}>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-fg-muted">Client name</Label>
                <Input value={r.name} disabled={!canEdit} className="mt-1"
                       data-testid={`gsoc-client-name-${i}`}
                       onChange={(e) => patch(r.id, { name: e.target.value })} />
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-fg-muted">Linked People &amp; Places client</Label>
                <select className={`${selectCls} mt-1`} value={r.linked_client_id || ''} disabled={!canEdit}
                        data-testid={`gsoc-client-link-${i}`}
                        onChange={(e) => patch(r.id, { linked_client_id: e.target.value })}>
                  <option value="">Not linked</option>
                  {linkable.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[240px]">
                <Label className="text-xs text-fg-muted">Report recipients (comma separated)</Label>
                <Input value={(r.report_recipients || []).join(', ')} disabled={!canEdit} className="mt-1"
                       data-testid={`gsoc-client-recipients-${i}`}
                       onChange={(e) => patch(r.id, { report_recipients: e.target.value.split(',') })} />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Switch checked={r.is_active !== false} disabled={!canEdit} data-testid={`gsoc-client-active-${i}`}
                        onCheckedChange={(v) => patch(r.id, { is_active: v })} />
                <span className="text-sm text-fg-muted">Active</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={r.activity_log_enabled !== false} disabled={!canEdit}
                        data-testid={`gsoc-client-enabled-${i}`}
                        onCheckedChange={(v) => patch(r.id, { activity_log_enabled: v })} />
                <span className="text-sm text-fg-muted">In the operator dropdown</span>
              </div>
              <p className="text-xs text-fg-muted" data-testid={`gsoc-client-counts-${i}`}>
                {r.site_count} site{r.site_count === 1 ? '' : 's'} · {r.entry_count} log entr{r.entry_count === 1 ? 'y' : 'ies'}
              </p>
              <div className="ml-auto flex items-center gap-2">
                {canEdit && <Button variant="outline" onClick={() => save(r)} data-testid={`gsoc-client-save-${i}`}><Save className="w-4 h-4 mr-1" />Save</Button>}
                {canDelete && (
                  <Button variant="ghost" size="sm" onClick={() => remove(r)}
                          disabled={r.entry_count > 0}
                          title={r.entry_count > 0 ? 'Has log entries — deactivate instead' : 'Delete this client'}
                          data-testid={`gsoc-client-delete-${i}`}>
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ── Sites ─────────────────────────────────────────────────────────────── */
function SitesTab({ clients, canEdit }) {
  const [clientId, setClientId] = useState('');
  const [sites, setSites] = useState([]);
  const [adding, setAdding] = useState({ name: '', address: '' });

  const load = (cid) => {
    if (!cid) { setSites([]); return; }
    api.get('/gsoc/sites', { params: { client_id: cid } })
      .then(({ data }) => setSites(data)).catch(() => setSites([]));
  };
  useEffect(() => { load(clientId); }, [clientId]);

  const add = async () => {
    if (!adding.name.trim()) return toast.error('Enter a site name');
    try {
      await api.post('/gsoc/sites', { client_id: clientId, name: adding.name.trim(), address: adding.address, is_active: true, report_recipients: [] });
      setAdding({ name: '', address: '' });
      load(clientId);
      toast.success('Site added');
    } catch (e) { err(e, 'Could not add the site'); }
  };

  const save = async (s) => {
    try {
      await api.put(`/gsoc/sites/${s.id}`, {
        client_id: s.client_id, name: s.name, address: s.address || '', is_active: s.is_active,
        report_recipients: (s.report_recipients || []).filter(Boolean),
      });
      toast.success('Site saved');
      load(clientId);
    } catch (e) { err(e, 'Could not save the site'); }
  };

  return (
    <Section title="GSOC Sites" hint="Monitored sites are owned by this module — separate from billing jobs and warehouse locations. Each site can email its report to its own people.">
      <div className="max-w-md">
        <Label className="text-sm font-semibold">Client</Label>
        <select className={`${selectCls} mt-1`} value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="gsoc-config-site-client">
          <option value="">Select a client...</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {clientId && (
        <>
          {canEdit && (
            <div className={`${cardCls} flex flex-wrap gap-3 items-end`}>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm">Site name</Label>
                <Input value={adding.name} onChange={(e) => setAdding((a) => ({ ...a, name: e.target.value }))}
                       placeholder="North Gate" className="mt-1" data-testid="gsoc-site-name-input" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <Label className="text-sm">Address (optional)</Label>
                <Input value={adding.address} onChange={(e) => setAdding((a) => ({ ...a, address: e.target.value }))}
                       className="mt-1" data-testid="gsoc-site-address-input" />
              </div>
              <Button onClick={add} data-testid="gsoc-site-add-btn"><Plus className="w-4 h-4 mr-1" />Add Site</Button>
            </div>
          )}

          <div className="space-y-2" data-testid="gsoc-sites-list">
            {sites.length === 0 && <p className="text-sm text-fg-muted" data-testid="gsoc-sites-empty">No sites yet for this client.</p>}
            {sites.map((s, i) => (
              <div key={s.id} className={`${cardCls} flex flex-wrap gap-3 items-end`} data-testid={`gsoc-site-row-${i}`}>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-fg-muted">Name</Label>
                  <Input value={s.name} disabled={!canEdit} className="mt-1"
                         onChange={(e) => setSites((list) => list.map((x) => x.id === s.id ? { ...x, name: e.target.value } : x))} />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs text-fg-muted">Address</Label>
                  <Input value={s.address || ''} disabled={!canEdit} className="mt-1"
                         onChange={(e) => setSites((list) => list.map((x) => x.id === s.id ? { ...x, address: e.target.value } : x))} />
                </div>
                <div className="flex-1 min-w-[240px]">
                  <Label className="text-xs text-fg-muted">Report recipients for this site (comma separated)</Label>
                  <Input value={(s.report_recipients || []).join(', ')} disabled={!canEdit} className="mt-1"
                         placeholder="northgate@client.com" data-testid={`gsoc-site-recipients-${i}`}
                         onChange={(e) => setSites((list) => list.map((x) => x.id === s.id
                           ? { ...x, report_recipients: e.target.value.split(',').map((v) => v.trim()) } : x))} />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Switch checked={s.is_active} disabled={!canEdit} data-testid={`gsoc-site-active-${i}`}
                          onCheckedChange={(v) => setSites((list) => list.map((x) => x.id === s.id ? { ...x, is_active: v } : x))} />
                  <span className="text-sm text-fg-muted">Active</span>
                </div>
                {canEdit && <Button variant="outline" onClick={() => save(s)} data-testid={`gsoc-site-save-${i}`}><Save className="w-4 h-4 mr-1" />Save</Button>}
              </div>
            ))}
          </div>
        </>
      )}
    </Section>
  );
}

/* ── Activity types ────────────────────────────────────────────────────── */
function ActivityTypesTab({ canEdit, canDelete }) {
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState('');

  const load = () => api.get('/gsoc/activity-types').then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!label.trim()) return toast.error('Enter a label');
    try {
      await api.post('/gsoc/activity-types', { label: label.trim(), is_active: true, sort_order: rows.length, client_id: null });
      setLabel(''); load(); toast.success('Activity type added');
    } catch (e) { err(e, 'Could not add the activity type'); }
  };

  const save = async (r) => {
    try {
      await api.put(`/gsoc/activity-types/${r.id}`, { label: r.label, is_active: r.is_active, sort_order: r.sort_order || 0, client_id: r.client_id || null });
      toast.success('Saved'); load();
    } catch (e) { err(e, 'Could not save'); }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/gsoc/activity-types/${r.id}`);
      toast.success(`"${r.label}" deleted`); load();
    } catch (e) { err(e, 'Could not delete the activity type'); }
  };

  const move = async (from, to) => {
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    try {
      await api.post('/gsoc/activity-types/reorder', { ids: next.map((x) => x.id) });
    } catch (e) { err(e, 'Could not save the new order'); load(); }
  };

  return (
    <Section title="Activity Types" hint="The dropdown the operator picks from, in the order shown here — use the arrows to reorder. Switch a type off to retire it and keep its label on historical entries; the delete button only works while nothing in the log uses it.">
      {canEdit && (
        <div className={`${cardCls} flex flex-wrap gap-3 items-end`}>
          <div className="flex-1 min-w-[240px]">
            <Label className="text-sm">New activity type</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1" data-testid="gsoc-type-label-input" />
          </div>
          <Button onClick={add} data-testid="gsoc-type-add-btn"><Plus className="w-4 h-4 mr-1" />Add Type</Button>
        </div>
      )}
      <div className="space-y-2" data-testid="gsoc-types-list">
        {rows.map((r, i) => (
          <div key={r.id} className={`${cardCls} flex flex-wrap gap-3 items-center`} data-testid={`gsoc-type-row-${i}`}>
            {canEdit && <ReorderButtons index={i} total={rows.length} onMove={move} testid={`gsoc-type-move-${i}`} />}
            <Input value={r.label} disabled={!canEdit} className="flex-1 min-w-[220px]"
                   onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, label: e.target.value } : x))} />
            <div className="flex items-center gap-2">
              <Switch checked={r.is_active} disabled={!canEdit} data-testid={`gsoc-type-active-${i}`}
                      onCheckedChange={(v) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, is_active: v } : x))} />
              <span className="text-sm text-fg-muted">Active</span>
            </div>
            {canEdit && <Button variant="outline" size="sm" onClick={() => save(r)} data-testid={`gsoc-type-save-${i}`}>Save</Button>}
            {canDelete && (
              <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`gsoc-type-delete-${i}`}>
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ── Canned comments ──────────────────────────────────────────────────── */
function CannedCommentsTab({ canEdit, canDelete }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState('');

  const load = () => api.get('/gsoc/canned-comments').then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!text.trim()) return toast.error('Enter the comment text');
    try {
      await api.post('/gsoc/canned-comments', { text: text.trim(), is_active: true, sort_order: rows.length, client_id: null, activity_type_id: null });
      setText(''); load(); toast.success('Quick comment added');
    } catch (e) { err(e, 'Could not add the comment'); }
  };

  const save = async (r) => {
    try {
      await api.put(`/gsoc/canned-comments/${r.id}`, { text: r.text, is_active: r.is_active, sort_order: r.sort_order || 0, client_id: r.client_id || null, activity_type_id: r.activity_type_id || null });
      toast.success('Saved'); load();
    } catch (e) { err(e, 'Could not save'); }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/gsoc/canned-comments/${r.id}`);
      toast.success('Quick comment deleted'); load();
    } catch (e) { err(e, 'Could not delete the quick comment'); }
  };

  const move = async (from, to) => {
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    try {
      await api.post('/gsoc/canned-comments/reorder', { ids: next.map((x) => x.id) });
    } catch (e) { err(e, 'Could not save the new order'); load(); }
  };

  return (
    <Section title="Quick Comments" hint="Prefills the comment box on the entry screen, in the order shown here — use the arrows to reorder. The operator can always edit the text before submitting.">
      {canEdit && (
        <div className={`${cardCls} flex flex-wrap gap-3 items-end`}>
          <div className="flex-1 min-w-[260px]">
            <Label className="text-sm">New quick comment</Label>
            <Input value={text} onChange={(e) => setText(e.target.value)} className="mt-1" data-testid="gsoc-canned-text-input" />
          </div>
          <Button onClick={add} data-testid="gsoc-canned-add-btn"><Plus className="w-4 h-4 mr-1" />Add Comment</Button>
        </div>
      )}
      <div className="space-y-2" data-testid="gsoc-canned-list">
        {rows.map((r, i) => (
          <div key={r.id} className={`${cardCls} flex flex-wrap gap-3 items-center`} data-testid={`gsoc-canned-row-${i}`}>
            {canEdit && <ReorderButtons index={i} total={rows.length} onMove={move} testid={`gsoc-canned-move-${i}`} />}
            <Input value={r.text} disabled={!canEdit} className="flex-1 min-w-[260px]"
                   onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, text: e.target.value } : x))} />
            <div className="flex items-center gap-2">
              <Switch checked={r.is_active} disabled={!canEdit} data-testid={`gsoc-canned-active-${i}`}
                      onCheckedChange={(v) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, is_active: v } : x))} />
              <span className="text-sm text-fg-muted">Active</span>
            </div>
            {canEdit && <Button variant="outline" size="sm" onClick={() => save(r)} data-testid={`gsoc-canned-save-${i}`}>Save</Button>}
            {canDelete && (
              <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`gsoc-canned-delete-${i}`}>
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ── Shifts ───────────────────────────────────────────────────────────── */
function ShiftsTab({ canEdit, canDelete }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ label: '', start_time: '07:00', end_time: '15:00' });

  const load = () => api.get('/gsoc/shifts').then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.label.trim()) return toast.error('Enter a shift name');
    try {
      await api.post('/gsoc/shifts', { ...draft, label: draft.label.trim(), is_active: true });
      setDraft({ label: '', start_time: '07:00', end_time: '15:00' }); load(); toast.success('Shift added');
    } catch (e) { err(e, 'Could not add the shift'); }
  };

  const save = async (r) => {
    try {
      await api.put(`/gsoc/shifts/${r.id}`, { label: r.label, start_time: r.start_time, end_time: r.end_time, is_active: r.is_active });
      toast.success('Saved'); load();
    } catch (e) { err(e, 'Could not save'); }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/gsoc/shifts/${r.id}`);
      toast.success(`"${r.label}" deleted`); load();
    } catch (e) { err(e, 'Could not delete the shift'); }
  };

  return (
    <Section title="Shift Windows" hint="Every entry is stamped with the shift its submission time falls into. Windows crossing midnight are supported.">
      {canEdit && (
        <div className={`${cardCls} flex flex-wrap gap-3 items-end`}>
          <div className="min-w-[160px] flex-1">
            <Label className="text-sm">Shift name</Label>
            <Input value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} className="mt-1" data-testid="gsoc-shift-label-input" />
          </div>
          <div>
            <Label className="text-sm">Starts</Label>
            <Input type="time" value={draft.start_time} onChange={(e) => setDraft((d) => ({ ...d, start_time: e.target.value }))} className="mt-1" data-testid="gsoc-shift-start-input" />
          </div>
          <div>
            <Label className="text-sm">Ends</Label>
            <Input type="time" value={draft.end_time} onChange={(e) => setDraft((d) => ({ ...d, end_time: e.target.value }))} className="mt-1" data-testid="gsoc-shift-end-input" />
          </div>
          <Button onClick={add} data-testid="gsoc-shift-add-btn"><Plus className="w-4 h-4 mr-1" />Add Shift</Button>
        </div>
      )}
      <div className="space-y-2" data-testid="gsoc-shifts-list">
        {rows.map((r, i) => (
          <div key={r.id} className={`${cardCls} flex flex-wrap gap-3 items-center`} data-testid={`gsoc-shift-row-${i}`}>
            <Input value={r.label} disabled={!canEdit} className="min-w-[160px] flex-1"
                   onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, label: e.target.value } : x))} />
            <Input type="time" value={r.start_time} disabled={!canEdit} className="w-32"
                   onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, start_time: e.target.value } : x))} />
            <Input type="time" value={r.end_time} disabled={!canEdit} className="w-32"
                   onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, end_time: e.target.value } : x))} />
            <div className="flex items-center gap-2">
              <Switch checked={r.is_active} disabled={!canEdit} data-testid={`gsoc-shift-active-${i}`}
                      onCheckedChange={(v) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, is_active: v } : x))} />
              <span className="text-sm text-fg-muted">Active</span>
            </div>
            {canEdit && <Button variant="outline" size="sm" onClick={() => save(r)} data-testid={`gsoc-shift-save-${i}`}>Save</Button>}
            {canDelete && (
              <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`gsoc-shift-delete-${i}`}>
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function GSOCConfig() {
  const perms = useEffectivePermissions();
  const canEdit = perms.role === 'admin' || perms.can('gsoc_config', 'edit');
  const canDelete = perms.role === 'admin' || perms.can('gsoc_config', 'delete');
  const [clientRows, setClientRows] = useState([]);

  // The GSOC client list is the source of truth; the settings call only carries
  // the report recipients / timezone / dropdown flag, merged in by id.
  const loadClients = async () => {
    try {
      const [{ data: clients }, { data: settings }] = await Promise.all([
        api.get('/gsoc/clients'),
        api.get('/gsoc/client-settings'),
      ]);
      const byId = Object.fromEntries(settings.map((s) => [s.client_id, s]));
      setClientRows(clients.map((c) => ({
        ...c,
        activity_log_enabled: byId[c.id]?.activity_log_enabled !== false,
        report_recipients: byId[c.id]?.report_recipients || [],
        timezone: byId[c.id]?.timezone || 'America/Chicago',
      })));
    } catch { /* permissions or offline — the tab shows its empty state */ }
  };
  useEffect(() => { loadClients(); }, []);

  return (
    <div className="space-y-5" data-testid="gsoc-config-page">
      <div>
        <h1 className="page-title">GSOC Configuration</h1>
        <p className="text-sm text-fg-muted mt-1">
          {canEdit ? 'Changes apply to new entries only — the existing record is never rewritten.'
                   : 'Read-only: your role can view this configuration but not change it.'}
        </p>
      </div>

      <Tabs defaultValue="clients">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="clients" data-testid="gsoc-tab-clients">Clients</TabsTrigger>
          <TabsTrigger value="sites" data-testid="gsoc-tab-sites">Sites</TabsTrigger>
          <TabsTrigger value="types" data-testid="gsoc-tab-types">Activity Types</TabsTrigger>
          <TabsTrigger value="canned" data-testid="gsoc-tab-canned">Quick Comments</TabsTrigger>
          <TabsTrigger value="shifts" data-testid="gsoc-tab-shifts">Shifts</TabsTrigger>
        </TabsList>
        <TabsContent value="clients" className="mt-4"><ClientsTab rows={clientRows} reload={loadClients} canEdit={canEdit} canDelete={canDelete} /></TabsContent>
        <TabsContent value="sites" className="mt-4"><SitesTab clients={clientRows} canEdit={canEdit} /></TabsContent>
        <TabsContent value="types" className="mt-4"><ActivityTypesTab canEdit={canEdit} canDelete={canDelete} /></TabsContent>
        <TabsContent value="canned" className="mt-4"><CannedCommentsTab canEdit={canEdit} canDelete={canDelete} /></TabsContent>
        <TabsContent value="shifts" className="mt-4"><ShiftsTab canEdit={canEdit} canDelete={canDelete} /></TabsContent>
      </Tabs>
    </div>
  );
}
